<template>
  <el-config-provider :locale="locale">
    <router-view />
  </el-config-provider>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import en from 'element-plus/es/locale/lang/en'
import { useThemeStore } from '@/features/settings/presentation/stores/theme.store'

const locale = ref(en)
const themeStore = useThemeStore()

onMounted(() => {
  themeStore.initializeTheme()
})
</script>

<style lang="scss">
// CSS Variables for theming
:root {
  // Light theme (default)
  --bg-primary: #ffffff;
  --bg-secondary: #f5f7fa;
  --bg-tertiary: #fafafa;
  --bg-hover: #f5f7fa;

  --text-primary: #303133;
  --text-secondary: #606266;
  --text-tertiary: #909399;
  --text-placeholder: #c0c4cc;

  --border-color: #e4e7ed;
  --border-light: #ebeef5;

  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);

  --primary-color: #409eff;
  --success-color: #67c23a;
  --warning-color: #e6a23c;
  --danger-color: #f56c6c;
  --info-color: #909399;

  --scrollbar-track: #f1f1f1;
  --scrollbar-thumb: #c0c4cc;
  --scrollbar-thumb-hover: #909399;
}

// Dark theme
html.dark {
  --bg-primary: #1a1a2e;
  --bg-secondary: #16213e;
  --bg-tertiary: #0f0f1a;
  --bg-hover: #252542;

  --text-primary: #e4e6eb;
  --text-secondary: #b0b3b8;
  --text-tertiary: #8a8d91;
  --text-placeholder: #6c6e72;

  --border-color: #3a3a5c;
  --border-light: #2d2d4a;

  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.3);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.4);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.5);

  --scrollbar-track: #1a1a2e;
  --scrollbar-thumb: #3a3a5c;
  --scrollbar-thumb-hover: #5a5a7c;

  // Element Plus dark overrides
  --el-bg-color: #1a1a2e;
  --el-bg-color-overlay: #252542;
  --el-bg-color-page: #16213e;
  --el-text-color-primary: #e4e6eb;
  --el-text-color-regular: #b0b3b8;
  --el-text-color-secondary: #8a8d91;
  --el-text-color-placeholder: #6c6e72;
  --el-border-color: #3a3a5c;
  --el-border-color-light: #2d2d4a;
  --el-border-color-lighter: #252542;
  --el-fill-color: #252542;
  --el-fill-color-light: #2d2d4a;
  --el-fill-color-lighter: #3a3a5c;
  --el-fill-color-blank: #1a1a2e;
  --el-mask-color: rgba(0, 0, 0, 0.7);
  --el-box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.4);
  --el-box-shadow-light: 0 1px 2px 0 rgba(0, 0, 0, 0.3);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  width: 100%;
  height: 100%;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    'Noto Color Emoji';
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-primary);
  background-color: var(--bg-secondary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  transition: background-color 0.3s, color 0.3s;
}

#app {
  min-height: 100vh;
}

// Scrollbar styling
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: var(--scrollbar-track);
}

::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb);
  border-radius: 3px;

  &:hover {
    background: var(--scrollbar-thumb-hover);
  }
}

// Element Plus overrides
.el-card {
  border-radius: 8px;
  background-color: var(--bg-primary);
  border-color: var(--border-color);
  transition: background-color 0.3s, border-color 0.3s;
}

.el-button {
  border-radius: 6px;
}

.el-tag {
  border-radius: 4px;
}

.el-dialog {
  border-radius: 12px;
  background-color: var(--bg-primary);
}

.el-message-box {
  border-radius: 12px;
  background-color: var(--bg-primary);
}

// Dark mode specific overrides
html.dark {
  .el-menu {
    background-color: var(--bg-primary);
    border-color: var(--border-color);
  }

  .el-menu-item {
    color: var(--text-secondary);

    &:hover {
      background-color: var(--bg-hover);
    }

    &.is-active {
      color: var(--primary-color);
      background-color: var(--bg-hover);
    }
  }

  .el-table {
    --el-table-bg-color: var(--bg-primary);
    --el-table-tr-bg-color: var(--bg-primary);
    --el-table-row-hover-bg-color: var(--bg-hover);
    --el-table-header-bg-color: var(--bg-secondary);
    --el-table-border-color: var(--border-color);
    background-color: var(--bg-primary);
  }

  .el-input__wrapper {
    background-color: var(--bg-secondary);
    box-shadow: 0 0 0 1px var(--border-color) inset;
  }

  .el-select__wrapper {
    background-color: var(--bg-secondary);
    box-shadow: 0 0 0 1px var(--border-color) inset;
  }

  .el-textarea__inner {
    background-color: var(--bg-secondary);
    box-shadow: 0 0 0 1px var(--border-color) inset;
  }

  .el-dropdown-menu {
    background-color: var(--bg-primary);
    border-color: var(--border-color);
  }

  .el-dropdown-menu__item {
    color: var(--text-secondary);

    &:hover {
      background-color: var(--bg-hover);
      color: var(--text-primary);
    }
  }

  .el-pagination {
    --el-pagination-bg-color: var(--bg-secondary);
    --el-pagination-button-bg-color: var(--bg-primary);
  }

  .el-alert {
    &.el-alert--warning {
      --el-alert-bg-color: rgba(230, 162, 60, 0.1);
    }
    &.el-alert--success {
      --el-alert-bg-color: rgba(103, 194, 58, 0.1);
    }
    &.el-alert--info {
      --el-alert-bg-color: rgba(144, 147, 153, 0.1);
    }
    &.el-alert--error {
      --el-alert-bg-color: rgba(245, 108, 108, 0.1);
    }
  }

  .el-breadcrumb__inner {
    color: var(--text-tertiary);
  }

  .el-breadcrumb__separator {
    color: var(--text-placeholder);
  }
}

// Utility classes
.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.font-bold {
  font-weight: 600;
}

.text-muted {
  color: var(--text-tertiary);
}

.mt-4 {
  margin-top: 16px;
}

.mb-4 {
  margin-bottom: 16px;
}

.ml-2 {
  margin-left: 8px;
}

.mr-2 {
  margin-right: 8px;
}
</style>
